
import { useQuery } from "@tanstack/react-query";
import { Modal, ModalBody, CardBody, Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from "reactstrap";
import { fetchLegacyDesignPacks, fetchDesignPackEntriesByDesignPackRevisionId } from "../data/legacyQuotesService";
import DataLoader from "../../../components/loader/DataLoader";
import { useTheme } from "../../../hooks/useTheme";
import { useState, useMemo, useEffect } from "react";
import { flushSync } from "react-dom";
import { toast } from 'react-toastify';
import DataTableWindowed from "../../../components/DataTableWindowed";
import LegacyQuotesImportModal from "./legacyQuotesImportModal";
import { fetchItemLookupByFilter } from "../../items/data/itemLookupService";
import { useLockState } from "../../../components/userLocks/LockCheck";

const REVISION_CONTROL_WIDTH = 120;

const ImportItemsButtonDropdown = ({ dp, onImportItems }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => setDropdownOpen(prev => !prev);

    const latestRevision = useMemo(() => {
        if (!dp.revisions || dp.revisions.length === 0) return null;
        return dp.revisions.reduce((latest, current) => {
            return current.revisionNumber > latest.revisionNumber ? current : latest;
        }, dp.revisions[0]);
    }, [dp.revisions]);

    const quoteRevisions = dp.revisions ?? [];
    const hasMultipleRevisions = quoteRevisions.length > 1;

    if (!latestRevision) return null;

    if (!hasMultipleRevisions) {
        return (
            <Button
                color="secondary"
                className="mb-0 text-start"
                style={{ width: `${REVISION_CONTROL_WIDTH}px` }}
                onClick={(e) => {
                    e.stopPropagation();
                    onImportItems(dp.designPackId, latestRevision.designPackRevisionId, latestRevision.revisionNumber);
                }}
            >
                Revision {latestRevision.revisionNumber}
            </Button>
        );
    }

    return (
        <ButtonDropdown
            isOpen={dropdownOpen}
            toggle={toggleDropdown}
            className="mb-0"
            style={{ width: `${REVISION_CONTROL_WIDTH}px` }}
            onClick={(e) => e.stopPropagation()}
        >
            <Button
                color="secondary"
                className="flex-grow-1 text-start text-truncate"
                onClick={(e) => {
                    e.stopPropagation();
                    onImportItems(dp.designPackId, latestRevision.designPackRevisionId, latestRevision.revisionNumber);
                }}
            >
                Revision {latestRevision.revisionNumber}
            </Button>

            <DropdownToggle
                caret
                split
                color="secondary"
                onClick={(e) => e.stopPropagation()}
                aria-label="Show revision options"
            />

            <DropdownMenu
                flip
                end
                container="body"
                strategy="fixed"
                style={{ minWidth: `${REVISION_CONTROL_WIDTH}px`, width: `${REVISION_CONTROL_WIDTH}px`, zIndex: 2000 }}
                onClick={(e) => e.stopPropagation()}
            >
                {[...quoteRevisions]
                    .sort((a, b) => {
                        // Sort by revision number descending, so that the latest revision is at the top of the list
                        return b.revisionNumber - a.revisionNumber;
                    })
                    .map((rev) => (
                        <DropdownItem
                            key={rev.designPackRevisionId}
                            className="text-start"
                            active={String(rev.designPackRevisionId) === String(latestRevision.designPackRevisionId)}
                            toggle={false}
                            onClick={(e) => {
                                e.stopPropagation();
                                onImportItems(dp.designPackId, rev.designPackRevisionId, rev.revisionNumber);
                            }}
                        >
                            Revision {rev?.revisionNumber}
                        </DropdownItem>
                    ))}
            </DropdownMenu>
        </ButtonDropdown>
    )
}

const LegacyQuotesTable = ({ addQuoteItems }) => {
    const lockState = useLockState();

    const [filter, setFilter] = useState({ pageNumber: 1, pageSize: 10 });
    const [isFilterChanging, setIsFilterChanging] = useState(false);
    // configuration for the detailed entries modal
    const [entriesModalOpen, setEntriesModalOpen] = useState(false);
    const toggleEntriesModal = () => setEntriesModalOpen((prev) => !prev);
    const [detailedEntriesData, setDetailedEntriesData] = useState([]);
    const [detailedEntriesModalTitle, setDetailedEntriesModalTitle] = useState(null);
    const [isLoadingEntries, setIsLoadingEntries] = useState(false);
    const theme = useTheme();

    // Get all catalogue items
    const PAGE_SIZE = 1000000;

    const getFullCatalogue = async () => {
        let pageNumber = 1;
        const all = [];

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const resp = await fetchItemLookupByFilter({
                pageNumber,
                pageSize: PAGE_SIZE,
                onlyShowActiveItems: "Active",
            });

            const batch = resp?.itemLookupItems ?? [];
            all.push(...batch);

            if (!resp?.totalRecords || all.length >= resp.totalRecords || batch.length < PAGE_SIZE) break;
            pageNumber += 1;
        }

        return all;
    };

    const {
        data: fullCatalogue = [],
        isFetched: isFullCatalogueFetched,
    } = useQuery({
        queryKey: ["fullItemCatalogue"],
        queryFn: getFullCatalogue,
        staleTime: 5 * 60 * 1000,
    });    
    
    // Fetch data using useQuery
    const getLegacyQuotesData = async () => {
        const items = await fetchLegacyDesignPacks();

        return items;
    }

    const {
        data: queryData,
        isFetching: isLoading
    } = useQuery({
        queryKey: ["legacyQuotesData", filter],
        queryFn: getLegacyQuotesData,
        staleTime: 0
    });

    const legacyQuotesData = queryData ?? [];

    // Reset filter changing state when data loads
    useEffect(() => {
        if (!isLoading && isFilterChanging) {
            setIsFilterChanging(false);
        }
    }, [isLoading, isFilterChanging]);

    const mapLegacyQuotesData = (data) => {
        
        return data.map(entry => {

            const num = (v) => {
                const parsed = Number(v);
                return Number.isFinite(parsed) ? parsed : 0;
            };

            const ooCost = num(entry.unitOoInternalCost) + num(entry.unitOoExternalCost);
            const ooOriginalPrice = num(entry.unitOoInternalPrice) + num(entry.unitOoExternalPrice);
            const ooPrice = entry.unitOoPriceOverride == null ? ooOriginalPrice : num(entry.unitOoPriceOverride);

            const arCostAnnual = num(entry.unitArInternalCost) + num(entry.unitArExternalCost);
            const arOriginalPriceAnnual = num(entry.unitArInternalPrice) + num(entry.unitArExternalPrice);
            const arPriceAnnual = entry.unitArPriceOverride == null ? arOriginalPriceAnnual : num(entry.unitArPriceOverride);

            const isRecurring = entry.isRecurring || 
                ( // MS in V1 was stored as upfront for some reason, but it's recurring in NG, so we need to change it
                    (entry.categoryName.startsWith("Managed Service") ||
                     entry.categoryName.startsWith("New Managed Service"))
                );

            // In V1, items were either upfront or recurring, but not both.  In NG, items can be both upfront and recurring
            // but v1 import items obviously have no need for that.  So, isUpfront is the opposite of isRecurring
            const isUpfront = !isRecurring;

            const isCreditItem = arPriceAnnual < 0 || ooPrice < 0;

            const itemId = String(entry?.itemId ?? "");
            const isPsItem = itemId.startsWith("PSCore.") || itemId.startsWith("PSOptions.");
            const nameOnQuote = isPsItem ? entry.description : (entry.nameOnQuote ?? entry.description);
            const internalName = isPsItem ? entry.autotaskCode : entry.description;

            const isMsItem = entry.categoryName.startsWith("Managed Service") || entry.categoryName.startsWith("New Managed Service");
            const isMsSummaryEntry = itemId.startsWith("NewManagedServiceCostSummary.") || itemId.startsWith("ManagedServiceCostSummary.");
            const isMsDetailEntry = isMsItem && !isMsSummaryEntry;
 
            return {
                id: entry.id,
                designPackRevisionId: entry.designPackRevisionId,
                internalName: internalName,
                nameOnQuote: nameOnQuote,
                upfrontNameOnQuote: isUpfront ? nameOnQuote : null,
                recurringNameOnQuote: isRecurring ? nameOnQuote : null,
                categoryName: entry.categoryName,
                disabled: !!entry.paused,
                autotaskCode: entry.autotaskCode,
                salesforceCode: entry.salesforceCode,
                upfrontSalesforceCode: isUpfront ? entry.salesforceCode || "" : "",
                recurringSalesforceCode: isRecurring ? entry.salesforceCode || "" : "",
                supplier: entry.supplier,
                isInternal: entry.costType?.toLowerCase() === "internal" && !entry.categoryName?.startsWith("Calls and Lines ("),
                isRecurring: isRecurring,
                isUpfront: isUpfront,
                isCreditItem: isCreditItem,
                recurringNumPayments: isRecurring ? entry.termLengthMonths : null, 
                recurringStartMonth: isRecurring ? 1 : null,
                recurringIntervalMonths: isRecurring ? 1 : null, // Assume monthly

                // Make sure all of the values are positive.  v1 represented credit items with
                // negative quantities and prices, but NG now represents credit items with positive 
                // quantities and prices, and a separate isCreditItem flag.
                //
                // Note that in V1, managed services items had both summary level and detail level entries with both
                // costs and prices. But the detail level items were only used for autotask export. They were excluded
                // from all calculations.  NG doesn't support excluding items from calculations, so if we imported them
                // as-is, we'd end up with double the cost/price that we should. We need to make sure that
                // we have the items for autotask export, but we must not double-count the cost/price.  So, when we import
                // we will put the costs against the detail level items, and the price against the summary level.  We
                // can't have the price per detail item, because the summary level is the one that's on the quote in V1,
                // and is the one that the user sets the price for.
                upfrontCost: !isMsSummaryEntry ? Math.abs(ooCost) : 0,
                upfrontPrice: !isMsDetailEntry ? Math.abs(ooPrice) : 0,
                recurringCost: !isMsSummaryEntry ? Math.abs(arCostAnnual) / 12 : 0, // Convert to monthly cost
                recurringPrice: !isMsDetailEntry ? Math.abs(arPriceAnnual) / 12 : 0, // Convert to monthly price
                originalUpfrontPrice: !isMsDetailEntry ? Math.abs(ooOriginalPrice) : 0,
                originalRecurringPrice: !isMsDetailEntry ? Math.abs(arOriginalPriceAnnual) / 12 : 0, // Convert to monthly price

                quantity: Math.abs(entry.quantity),

                itemId: entry.itemId,
                itemType: entry.itemId ? entry.itemId.split('.')[0] : undefined,
                typeCustomAttributes: entry.typeCustomAttributes
            };

        });
    }

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        // Set loading state when filter changes (except for pagination)
        if (filterId !== "pageNumber" && filterId !== "pageSize") {
            setIsFilterChanging(true);
        }

        if (filterId === "pageNumber") {
            setFilter(prev => ({ ...prev, [filterId]: value }));
        } else if (filterId === "timestamp") {
            if (value.from && value.to && value.from > value.to) {
                toast.warning("Filter error: the start date must be earlier than the end date");
            }
            setFilter(prev => ({
                ...prev,
                startDate: value.from === "" ? undefined : value.from,
                endDate: value.to === "" ? undefined : value.to,
                pageNumber: 1
            }));        
        } else {
            if (!value) {
                setFilter(prev => {
                    const { [obj.filterId]: _, ...rest } = prev;
                    return { ...rest, pageNumber: 1 };
                });
            } else {
                setFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
            }
        }
    }

    const openModalForRevision = async (designPackId, revisionId, revisionNumber) => {
        if (lockState?.isLocked) {
            toast.warning("The quote is locked, so you cannot import items");
            return;
        }

        flushSync(() => setIsLoadingEntries(true));
        const result = await fetchDesignPackEntriesByDesignPackRevisionId(revisionId);

        const shouldInclude = (item) => {
            const itemId = String(item?.itemId ?? "");
            if (item.autotaskCode === "iQuote Calls and Lines"
            ) {
                // This is a summary level line, we don't want to include it, as the detail level items already are included and would
                // cause a double-counting of value.
                return false;
            } else if (itemId.startsWith("Circuits") && item.isUpfront && item.upfrontCost === 0 && item.upfrontPrice === 0) {
                // Remove any circuits marked as upfront but had no upfront cost or price, as these likely were items that had no
                // install cost
                return false;
            }
            return true;
        }

        const entries = mapLegacyQuotesData(result.entries ?? []).filter(x => shouldInclude(x));
        
        setDetailedEntriesModalTitle(`Design Pack ${designPackId}, Revision ${revisionNumber}`);

        setDetailedEntriesData(entries.map(entry => ({
            id: entry.id,
            internalName: entry.internalName,
            nameOnQuote: entry.nameOnQuote,
            upfrontNameOnQuote: entry.upfrontNameOnQuote,
            recurringNameOnQuote: entry.recurringNameOnQuote,
            upfrontSalesforceCode: entry.upfrontSalesforceCode,
            recurringSalesforceCode: entry.recurringSalesforceCode,
            isInternal: entry.isInternal,
            designPackRevisionId: entry.designPackRevisionId,
            autotaskCode: entry.autotaskCode,
            salesforceCode: entry.salesforceCode,
            supplier: entry.supplier,
            disabled: !!entry.disabled,
            categoryName: entry.categoryName,
            groupName: entry.categoryName,
            isRecurring: !!entry.isRecurring,
            isUpfront: !!entry.isUpfront,
            isCreditItem: entry.isCreditItem,
            recurringNumPayments: entry.recurringNumPayments,
            recurringStartMonth: 1,
            recurringIntervalMonths: 1,
            originalUpfrontPrice: entry.originalUpfrontPrice,
            upfrontCost: entry.upfrontCost,
            upfrontPrice: entry.upfrontPrice,
            originalRecurringPrice: entry.originalRecurringPrice,
            recurringCost: entry.recurringCost,
            recurringPrice: entry.recurringPrice,
            itemId: entry.itemId,
            itemType: entry.itemType,
            quantity: entry.quantity,
            typeCustomAttributes: entry.typeCustomAttributes
        })));
        setEntriesModalOpen(true);
    }

    const onRowClick = async (rowData) => {
        if (lockState?.isLocked) {
            toast.warning("The quote is locked, so you cannot import items");
            return;
        }   

        const revisions = rowData.revisions ?? [];

        if (revisions.length === 1) {
            await openModalForRevision(rowData.designPackId, revisions[0].designPackRevisionId, revisions[0].revisionNumber);
        } else if (revisions.length > 1) {
            // If there are multiple revisions, we need to find the latest revision and open that one, but also inform the user
            // that there are multiple revisions
            toast.info("This design pack has multiple revisions. Opening latest revision");            
            const latestRevision = revisions.reduce((latest, current) => {
                return current.revisionNumber > latest.revisionNumber ? current : latest;
            }, revisions[0]);
            await openModalForRevision(rowData.designPackId, latestRevision.designPackRevisionId, latestRevision.revisionNumber);
        } else if (revisions.length === 0) {
            toast.info("No entries to show for this design pack, as it has no revisions");
        }
    }

    const columns = [
        {
            key: "opportunityNumber",
            label: "Opportunity Number",
            align: "left",
            style: { width: "200px" },
            render: (value, item) => {
                return <span className="ms-4">{value}</span>;
            }
        },
        {
            key: "accountName",
            label: "Account Name",
            align: "left",
            style: { width: "400px" }
        },
        {
            key: "opportunityName",
            label: "Opportunity Name",
            align: "left",
            style: { width: "400px" }
        },       
        {
            key: "designPackId",
            label: "Design Pack Id",
            render: (value, item) => {                
                return item.revisionNumber ? "" : value; // Hide the id column for child rows
            }
        },
        {
            key: "revisions",
            label: "Revision To Import",
            align: "right",
            style: { width: `${REVISION_CONTROL_WIDTH}px` },
            isSortDisabled: true,
            isFixed: true,
            fixDirection: "right",
            render: (value, item) => {
                return <ImportItemsButtonDropdown dp={item} onImportItems={openModalForRevision} />;
            }
        }
    ].map(col => ({ ...col, group: "" })); // Add default group for all columns;
    const showTableLoader = isLoading;

    return (
        <>
            <div style={{ position: "relative" }}>
            {showTableLoader && (
                <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    background: theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff",
                    opacity: 0.8,
                    zIndex: 10
                }}>
                    <DataLoader />
                </div>
            )}
            {isLoadingEntries && (
                <div style={{
                    position: "fixed", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 1050
                }}>
                    <div style={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                        padding: "24px 32px",
                        borderRadius: "8px",
                        background: theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                        border: theme === "dark-mode" ? "2px solid rgba(255,255,255,0.65)" : "2px solid #212529"
                    }}>
                        <Spinner color="primary" />
                        <span className="text-muted">Loading design pack entries…</span>
                    </div>
                </div>
            )}
            <CardBody>
                <DataTableWindowed
                    id="legacyQuotesTable"
                    columns={columns}
                    columnProfiles={{
                        "": columns.map(col => ({
                            key: col.key,
                            isDefault: true,
                            isRequired: false
                        }))
                    }}
                    currentProfileKey=""
                    data={(isLoading || isFilterChanging) ? [] : (legacyQuotesData ?? [])}
                    height="600px"                    
                    error={false}
                    onFilterChange={changeFilter}
                    exportToCsv={false}
                    onRowClick={onRowClick}
                    searchableFields={['opportunityNumber', 'opportunityName', 'accountName', 'owner', 'designPackId', 'quoteId']}
                    showDataLoaderText={false}
                    showDataLoaderTransitionEffect={true}
                    // searchableFields={['id', 'quoteId', 'opportunityNumber', 'opportunityName', 'accountName', 'owner']}
                    filters={/*[
                        {
                            id: "opportunityNumber",
                            label: "Opportunity Number",
                            type: "text",
                            allowPartialMatches: true,
                            isSearchable: true,
                            advanced: true,
                            column: 1
                        },
                        {
                            id: "opportunityName",
                            label: "Opportunity Name",
                            allowPartialMatches: true,
                            type: "text",
                            isSearchable: true,
                            advanced: true,
                            column: 1
                        },
                        {
                            id: "designPackId",
                            label: "Design Pack Id",
                            type: "text",
                            isSearchable: true,
                            advanced: true,
                            column: 1
                        },
                        {
                            id: "quoteId",
                            label: "Quote Id",
                            type: "text",
                            isSearchable: true,
                            advanced: true,
                            column: 1
                        },
                        {
                            id: "accountName",
                            label: "Account Name",
                            type: "text",
                            allowPartialMatches: true,
                            isSearchable: true,
                            advanced: true,
                            column: 2
                        },
                        {
                            id: "owner",
                            label: "Owner",
                            type: "text",
                            allowPartialMatches: true,
                            isSearchable: true,
                            advanced: true,
                            column: 2
                        }
                    ]*/
                   undefined}
                    // actions={[
                    //     <Button
                    //         className={`mt-2 ${lockState?.isLocked ? "locked" : ""}`}
                    //         // onClick={onClickAddCustomItem}
                    //     >
                    //         Search
                    //     </Button>                        
                    // ]}                    
                />
            </CardBody>
            </div>

            <Modal isOpen={entriesModalOpen && isFullCatalogueFetched} toggle={toggleEntriesModal} onOpened={() => setIsLoadingEntries(false)} backdrop="static" fullscreen>
                <ModalBody
                    style={{
                        height: "97.8vh",
                        overflow: "hidden",
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    <LegacyQuotesImportModal
                        toggle={toggleEntriesModal}
                        items={detailedEntriesData}
                        title={detailedEntriesModalTitle}
                        addQuoteItems={addQuoteItems}
                        fullCatalogue={fullCatalogue}
                    />
                </ModalBody>
            </Modal>
        </>
    );
}
export default LegacyQuotesTable;
